import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Select } from '@/components/ui/select';
import { errorMessage } from '@/lib/errors';
import { todayInput, useCreateFinanceBankCheck } from '@/lib/finance';
import { formatMinor, toMinor } from '@/lib/money';
import type { FinanceAccount, FinanceBankCheck } from '@/api/generated/types.gen';

export interface BankCheckDialogProps {
  account: FinanceAccount;
  businessTz?: string;
  onClose: () => void;
}

/**
 * File the bank's own balance for a pair on a date next to ours, and show the
 * difference the server computed. Deliberately NOT idempotent — a second
 * check IS a second reconciliation — so this dialog carries no operation_id
 * and closes only after the operator has read the answer.
 */
export function BankCheckDialog({ account, businessTz, onClose }: BankCheckDialogProps) {
  const [currency, setCurrency] = useState(account.currencies[0] ?? '');
  const [asOf, setAsOf] = useState(() => todayInput(businessTz));
  const [bank, setBank] = useState('');
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [result, setResult] = useState<FinanceBankCheck | null>(null);
  const mut = useCreateFinanceBankCheck();

  const minor = currency ? toMinor(bank, currency) : null;
  const ready = !!currency && !!asOf && minor !== null;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Reconcile ${account.name}`}
      description="Compare the bank statement balance with the recorded balance for the same currency and date. Saving a check does not change either balance."
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!ready}
              loading={mut.isPending}
              onClick={() => {
                if (minor === null) return;
                setFailure(null);
                mut.mutate(
                  {
                    account_id: account.id,
                    currency,
                    as_of: asOf,
                    bank_balance_minor: minor,
                    ...(note.trim() ? { note: note.trim() } : {}),
                  },
                  { onSuccess: setResult, onError: (e) => setFailure(errorMessage(e)) },
                );
              }}
            >
              Compare balances
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div role="status" className="flex flex-col gap-4">
        <div className="rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-4">
          <h3 className="font-semibold">{result.diff_minor === 0 ? 'Balances match' : 'Difference needs review'}</h3>
          <p className="mt-1 text-sm text-muted">Check saved for {result.as_of} · {result.currency}</p>
        </div>
        <dl className="flex flex-col gap-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Bank statement</dt>
            <dd className="tabular-nums">
              {formatMinor(result.bank_balance_minor, result.currency)}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Recorded balance</dt>
            <dd className="tabular-nums">
              {formatMinor(result.computed_balance_minor, result.currency)}
            </dd>
          </div>
          <div className="flex justify-between gap-4 border-t border-[var(--border-hairline)] pt-2 font-semibold">
            <dt>Difference</dt>
            <dd
              className={
                result.diff_minor === 0 ? 'tabular-nums text-[var(--ollio-success)]' : 'tabular-nums text-destructive'
              }
            >
              {formatMinor(result.diff_minor, result.currency)}
            </dd>
          </div>
        </dl>
        {result.diff_minor !== 0 && (
          <p className="text-sm text-muted">Compare the account transactions with your statement for this date. Check for missing or duplicate payments, fees and differences in timing before making a correction.</p>
        )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Select
            label="Currency"
            value={currency}
            options={account.currencies.map((c) => ({ value: c, label: c }))}
            onChange={(e) => setCurrency(e.target.value)}
          />
          <Input label="As of" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <MoneyInput
            label="Balance the bank shows"
            currency={currency || 'EUR'}
            value={bank}
            onChange={setBank}
            allowNegative
          />
          <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
          {failure && (
            <p role="alert" className="text-sm text-destructive">
              {failure}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
